import React, {ReactElement} from 'react';
import {
  Button,
  createStyles,
  makeStyles,
  Theme,
  Typography
} from '@material-ui/core';
import {useDispatch, useSelector} from 'react-redux';

// styles
import './PaymentsCardStyles.scss';
import {APP_PRIMARY_COLOR, BORDER_COLOR} from 'src/style/variable';
import CurrencyFormatter from '@components/system/CurrencyFormatter/CurrencyFormatter';
import HelperUtils from '@shared/utils/HelperUtils';
import {useTranslation} from 'react-i18next';
import {PatientClinicInterface} from '@shared/services/service-mappers/PaymentServiceMapper';
import {appointmentsDateTimeFormat} from '@shared/utils/DateTimeUtils';
import {State} from '@shared/store/reducers/Index';
import NavRoutes from 'src/constants/route/NavRoutes';
import {useHistory} from 'react-router-dom';
import {_patientPaymentDetailsId} from '@shared/store/actions/PaymentActions';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    defaultBorder: {
      border: `1px solid ${BORDER_COLOR}`
    },
    selectedBorderColor: {
      border: `2px solid ${APP_PRIMARY_COLOR}`
    }
  })
);

const {innerWidth: width} = window;
const isDesktop = width >= 1024 ? true : false;
const PaymentsCard = (props: {
  border?: string;
  name: string;
  due: string;
  accountName?: string;
  date?: string;
  detailsId?: number;
  copayAmount?: number;
  paidAmount?: number;
  clinic?: PatientClinicInterface;
  onClick?(sectionId?: string): void;
  patientId?: number;
  showAppointentAndPriorAuthDetails?: boolean;
  isSelected?: boolean;
  token?: string;
  patientDetails?: any;
}): ReactElement => {
  const {
    name,
    due,
    accountName,
    date,
    border,
    copayAmount,
    onClick,
    paidAmount,
    clinic,
    patientId,
    showAppointentAndPriorAuthDetails = false,
    isSelected = false,
    token,
    patientDetails
  } = props;
  const classes = useStyles();
  const {t} = useTranslation();
  const clinicDetails = useSelector(
    (state: any) => state.payments.clinicDetails
  );

  const patientApptAndTicketSummary = useSelector(
    (state: State) => state.payments.patientApptAndTicketSummary
  );

  const selectedPatientSummary = patientApptAndTicketSummary.find(
    patientSummary =>
      patientSummary.id === patientId && patientSummary.clinic_id === clinic?.id
  );
  const statementsEnable: boolean = useSelector(
    (state: any) =>
      state.payments.clinicDetails?.clinic_group_details?.statements || false
  );

  const history = useHistory();
  const dispatch = useDispatch();

  const hasActivePriorAuthorization =
    selectedPatientSummary?.active_prior_authorizations_count &&
    // @ts-ignore
    selectedPatientSummary?.active_prior_authorizations_count != 0 &&
    selectedPatientSummary?.active_prior_authorizations_count != '0';

  const hasPastPriorAuthorization =
    selectedPatientSummary?.past_prior_authorizations_count &&
    // @ts-ignore
    selectedPatientSummary?.past_prior_authorizations_count != 0 &&
    selectedPatientSummary?.past_prior_authorizations_count != '0';

  return (
    <Typography className="cards-view input-header" component={'div'}>
      <div
        className={[
          'card-main-view',
          isSelected ? classes.selectedBorderColor : classes.defaultBorder,
          isSelected ? 'selected-card-main-view' : '',
          !isDesktop ? 'mobile-view' : ''
        ].join(' ')}
        style={{border: border}}>
        {typeof onClick === 'function' && statementsEnable && (
          <Button
            onClick={e => {
              if (typeof onClick === 'function') {
                onClick();
                e.stopPropagation();
              }
            }}
            style={{float: 'right', fontWeight: 'bold'}}
            size={'small'}
            variant={'outlined'}
            color="primary">
            {t('PAYMENTS_LOGIN.DASHBOARD.VIEW_STATEMENT')}
          </Button>
        )}
        {(accountName || date) && (
          <div className="headers-section-main-view">
            {accountName && (
              <div
                className="header-left-view "
                data-test-id="pcl-guarantor-accountNumber">
                {t('PAYMENTS_LOGIN.DASHBOARD.ACCOUNT', {accountName})}
              </div>
            )}
            {date && (
              <div className="headers-right-view">
                {HelperUtils.formatDisplayDate(date)}
              </div>
            )}
          </div>
        )}
        <div className="values-section-main-view">
          <div
            className="values-left-view"
            data-test-id="pcl-guarantor-guarantorName">
            {name}
          </div>
        </div>
        <div className="values-section-main-view outstanding-balance-wrapper">
          {statementsEnable && (
            <div className="statement-dues">
              <span>{`${t('PAYMENTS_LOGIN.DASHBOARD.DUE') as string}: `}</span>
              <CurrencyFormatter
                value={due ?? 0}
                displayType={'text'}
                thousandSeparator={true}
                prefix={'$'}
                ignoreRound={true}
                useSign={true}
              />
            </div>
          )}
          {Number(copayAmount) ? (
            <div className="copay-dues">
              <span>
                {`${t('AD_HOC.COPAY_OR_OTHER_CHARGE_AMOUNT') as string}: `}
              </span>
              <CurrencyFormatter
                value={copayAmount ?? 0}
                displayType={'text'}
                thousandSeparator={true}
                prefix={'$'}
                ignoreRound={true}
                useSign={true}
              />
            </div>
          ) : (
            ''
          )}

          {Number(paidAmount) ? (
            <div>
              <span>{`${t('PAYMENT_HISTORY.PAID_AMOUNT') as string}: `}</span>
              <CurrencyFormatter
                value={paidAmount ?? 0}
                displayType={'text'}
                thousandSeparator={true}
                prefix={'$'}
                ignoreRound={true}
                useSign={true}
              />
            </div>
          ) : (
            ''
          )}
        </div>
        {clinic && clinic.name && (
          <div className="values-section-main-view value-margin">
            <span>
              <b>{`${t('PAYMENT_HISTORY.CLINIC') as string}: `}</b>
            </span>
            <span>{clinic.name}</span>
          </div>
        )}
        {showAppointentAndPriorAuthDetails && (
          <>
            {clinicDetails?.clinic_group_details?.appointments && (
              <div className="values-section-main-view value-margin appointments">
                <div>
                  <span>
                    <b>
                      {t('PAYMENTS_LOGIN.DASHBOARD.NEXT_APPOINTMENT_LABEL')}:{' '}
                    </b>
                  </span>
                  <span>
                    {selectedPatientSummary?.next_appointment_date
                      ? appointmentsDateTimeFormat(
                          selectedPatientSummary?.next_appointment_date,
                          'America/Chicago'
                        )
                      : t('COMMON.NONE_VALUE')}
                  </span>
                </div>
                <p
                  className="link-style view-appointments-link"
                  onClick={e => {
                    if (typeof onClick === 'function') {
                      onClick('appointments');
                    }
                    e.stopPropagation();
                  }}>
                  {t('COMMON.VIEW_LABEL')}
                </p>
              </div>
            )}
            {clinicDetails?.clinic_group_details
              ?.prior_authorizations_tickets &&
              (hasActivePriorAuthorization || hasPastPriorAuthorization) && (
                <div className="values-section-main-view value-margin prior-auth">
                  <span>
                    <b>
                      {t('PAYMENTS_LOGIN.DASHBOARD.PRIOR_AUTHORIZATION_LABEL')}:{' '}
                    </b>
                  </span>
                  {hasActivePriorAuthorization ? (
                    <div className="prior-auth-status">
                      {
                        selectedPatientSummary?.active_prior_authorizations_count
                      }
                    </div>
                  ) : (
                    <div>
                      {t(
                        'PAYMENTS_LOGIN.DASHBOARD.NO_CURRENT_PRIOR_AUTHORIZATION'
                      )}
                    </div>
                  )}
                  <p
                    className="link-style"
                    onClick={e => {
                      if (typeof onClick === 'function') {
                        onClick('prior-authorizations');
                        e.stopPropagation();
                      }
                    }}>
                    {t('COMMON.VIEW_LABEL')}
                  </p>
                </div>
              )}
            {clinicDetails?.clinic_group_details?.patient_forms &&
              selectedPatientSummary?.patient_form_status === 'sent' && (
                <div className="value-margin">
                  <p
                    className="link-style"
                    onClick={e => {
                      if (token) {
                        history.push(
                          `/${NavRoutes.PUBLIC.PAYMENTS_DETAILS}/${token}/patient-forms`
                        );
                        dispatch(_patientPaymentDetailsId(patientDetails));
                      }
                      e.stopPropagation();
                    }}>
                    {t('PAYMENTS_LOGIN.DASHBOARD.SUBMIT_MEDICARE_FORM_LABEL')}
                  </p>
                </div>
              )}
          </>
        )}
      </div>
    </Typography>
  );
};

export default PaymentsCard;
